import { FunctionComponent, memo, useCallback, useEffect, useMemo } from 'react';

import classNames from 'classnames';
import classes from './Tabs.module.scss';

export interface TabItem {
  index: number;
  name: string;
  content?: JSX.Element;
  action?: JSX.Element;
}

interface TabsProps {
  items: TabItem[];
  selectedTabIndex: number;
  setSelectedTabIndex: (index: number) => void;
  noBackground?: boolean;
  scrollable?: boolean;
  disabled?: boolean;
  className?: string;
  tabNamesClassName?: string;
  tabNamesItemClassName?: string;
  tabNamesStyle?: React.CSSProperties;
  tabContentClassName?: string;
}

export const Tabs: FunctionComponent<TabsProps> = memo(
  ({
    items,
    selectedTabIndex,
    setSelectedTabIndex,
    noBackground,
    scrollable,
    disabled,
    className,
    tabNamesClassName,
    tabNamesItemClassName,
    tabNamesStyle,
    tabContentClassName,
  }) => {
    useEffect(() => {
      if (items.length && !items.find(({ index }) => index === selectedTabIndex)) {
        setSelectedTabIndex(items[0].index);
      }
    }, [items, selectedTabIndex, setSelectedTabIndex]);

    const content = useMemo(
      () => items.find(({ index }) => index === selectedTabIndex)?.content,
      [items, selectedTabIndex]
    );

    const tabName = useCallback(
      ({ index, name, action }: TabItem) => {
        const isSelected = index === selectedTabIndex;

        const nameClassName = classNames(classes['tabs__tab-names-item-label'], {
          [classes['tabs__tab-names-item-label--selected']]: isSelected,
        });

        if (items.length === 1 && !action) {
          return <span className={nameClassName}>{name}</span>;
        }

        return (
          <button
            type={'button'}
            className={classNames(nameClassName, classes['tabs__tab-names-item-label--clickable'])}
            aria-pressed={isSelected}
            disabled={disabled}
            onClick={() => setSelectedTabIndex(index)}
            onFocus={({ currentTarget }) =>
              currentTarget.scrollIntoView({ block: 'nearest', inline: 'nearest' })
            }
          >
            {name}
          </button>
        );
      },
      [disabled, items.length, selectedTabIndex, setSelectedTabIndex]
    );

    return (
      <div className={classNames(classes['tabs'], className)}>
        <div
          className={classNames(
            classes['tabs__tab-names'],
            { [classes['tabs__tab-names--no-background']]: noBackground },
            { [classes['tabs__tab-names--scrollable']]: scrollable },
            tabNamesClassName
          )}
          style={tabNamesStyle}
        >
          {items.map(({ index, name, action }) => (
            <div
              key={index}
              className={classNames(
                classes['tabs__tab-names-item'],
                { [classes['tabs__tab-names-item--disabled']]: disabled },
                tabNamesItemClassName
              )}
            >
              {tabName({ index, name, action })}
              {Boolean(action) && (
                <div className={classes['tabs__tab-names-item-action']}>{action}</div>
              )}
            </div>
          ))}
        </div>
        {Boolean(content) && (
          <div className={classNames(classes['tabs__tab-content'], tabContentClassName)}>
            {content}
          </div>
        )}
      </div>
    );
  }
);
